// A deploy installs a new service worker that waits behind the old one, so the old build keeps loading
// (a reload doesn't swap them). This notices the waiting worker, offers a reload, and on "Reload"
// activates it; every open tab then reloads onto the new version.
import { useSyncExternalStore } from "react";

const CHECK_EVERY_MS = 60 * 60 * 1000;

let waiting: ServiceWorker | null = null;
const listeners = new Set<() => void>();

function setWaiting(worker: ServiceWorker) {
  waiting = worker;
  for (const l of listeners) l();
}

export function watchForUpdates(
  reg: ServiceWorkerRegistration,
  container: ServiceWorkerContainer = navigator.serviceWorker,
  reload: () => void = () => window.location.reload(),
): void {
  // Without a controller this is the first install, not an update.
  const hadController = !!container.controller;
  const track = (worker: ServiceWorker | null) => {
    if (!worker) return;
    if (worker.state === "installed") {
      if (container.controller) setWaiting(worker);
      return;
    }
    worker.addEventListener("statechange", () => {
      if (worker.state === "installed" && container.controller) setWaiting(worker);
    });
  };
  track(reg.waiting ?? reg.installing);
  reg.addEventListener("updatefound", () => track(reg.installing));

  // Open tabs and installed apps otherwise only look for a new version when they navigate.
  const check = () => void reg.update().catch(() => {});
  setInterval(check, CHECK_EVERY_MS);
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") check();
  });

  let reloading = false;
  container.addEventListener("controllerchange", () => {
    if (!hadController || reloading) return;
    reloading = true;
    reload();
  });
}

/** Activates the waiting version; the "controllerchange" handler then reloads. */
export function applyUpdate(): void {
  waiting?.postMessage({ type: "SKIP_WAITING" });
}

export function useUpdateAvailable(): boolean {
  return useSyncExternalStore(
    (l) => {
      listeners.add(l);
      return () => listeners.delete(l);
    },
    () => waiting !== null,
  );
}
